<template>
    <div class="account-page-padding">
        <form class="lists">
            <div class="header">
                <h1>Saved Lists</h1>
            </div>
            <p class="paragraph-feature">
                Access your previously created lists and carts
            </p>
            <div v-if="is_dealer && accountLevel === 'Administrator'" class="search-fields">
                <div class="user-search">
                    <div class="selectore-state select-wrapper">
                        <custom-select name="user_filter" id="user_filter"
                            label="View Lists for User..." v-model="userSearch" :options="userFilterOptions"/>
                    </div>
                </div>
            </div>
            <div class="search-fields">
                <div>
                    <custom-select name="lists_filter" id="lists_filter" 
                            label="Lists Filter" v-model="listsFilter" :options="listsFilterOptions"/>
                </div>
                <div>
                    <div class="field-state input-folder">
                        <div class="input-folder--input">
                            <PrimeInputText
                                v-model="searchListName"
                                type="search" 
                                id="search_input"
                                ref="search_input" 
                                placeholder=" "
                                maxlength="100"
                            />
                        </div>
                        <label for="search_input">
                            <p class="paragraph-default">Lists Search</p>
                        </label>
                    </div>
                </div>
                <div class="buttons">
                    <RothcoCta
                        id="search_button"
                        ref="search_button" 
                        type="submit"
                        button_icon="search"
                        :inertia="false"
                        @click.prevent="setListNameFilter()">
                        Search
                    </RothcoCta>
                    <RothcoCta
                        id="mass_delete_button" 
                        :button_icon="null" 
                        :inertia="false" 
                        :disabled="!hasListsChecked" 
                        :class="{'secondary': hasListsChecked, 'disabled': !hasListsChecked}" 
                        @click.prevent="openMassDeleteLists()"
                    >
                        Delete Lists({{ checked.length }})
                    </RothcoCta>
                    <RothcoCta 
                        id="merge_button" 
                        :button_icon="null" 
                        :inertia="false" 
                        :disabled="!hasMultipleListsChecked" 
                        :class="{'secondary': hasMultipleListsChecked, 'disabled': !hasMultipleListsChecked}" 
                        @click.prevent="openMergeListsModal()"
                    >
                        Merge Lists({{ checked.length }})
                    </RothcoCta>
                </div>
            </div>
        </form>
        <rothco-table
            key="table" id="lists_table"
            v-model:focused="focused"
            v-model:content="tableContent"
            :visible-fields="visibleFields"
            :backendPagination="backendPagination"
            :useBackendSorting="true"
            checkable
            checkableOnMobile="true"
            :check-all="true"
            v-model:checkedRows="checked"
            primary-key="list_id"
            focusable
        >
            <template v-for="(list, index) in tableContent" v-slot:[`list_name_${index}`]="slotProps">
                <div :key="`${list.data.list_id}-${index}`">
                    <Link :href="route('account.list', [list.data.list_id])">{{ list.data.list_name }}</Link>
                    <p class="paragraph-details">
                        {{ `${list.data.total_quantity} quantity${list.data.total_quantity != 1 ? '' : ''}` }} &nbsp; 
                        {{ `${list.data.total_skus} sku${list.data.total_skus != 1 ? 's' : ''}` }} &nbsp; 
                        {{ canSeePricing ? `$${parseFloat(list.data.total ?? 0).toFixed(2)}` : '' }}
                    </p>
                </div>
            </template>

            <template v-for="(list, index) in tableContent" v-slot:[`is_current_${index}`]="slotProps" >
                <div class="flex-column justify-center" :key="`${list.data.list_id}-${index}`">
                    <input type="checkbox"
                        style="pointer-events: none;"
                        :checked="list.data.is_current"
                    />
                </div>
            </template>

            <template v-for="(list, index) in tableContent" v-slot:[`rename_${index}`]="slotProps" >
                <div class="flex-column justify-center" :key="`${list.data.list_id}-${index}`">
                    <button class="secondary small" @click="openRenameList(list.data)">
                        <!-- Need better icon for this -->
                        <PlusIcon class="secondary" style="margin:0;"/>
                    </button>
                </div>
            </template>

            <template v-for="(list, index) in tableContent" v-slot:[`delete_${index}`]="slotProps">
                <div class="flex-column justify-center"  :key="`${list.data.list_id}-${index}`">
                    <button class="secondary small" @click="openDeleteList(list.data)">
                        <DeleteBlackIcon class="secondary" style="margin:0;"/>
                    </button>
                </div>
            </template>
        </rothco-table>
    </div>
</template>
<script setup lang="ts">
    import RothcoTable from "@/components/RothcoTable.vue";
    import RothcoCta from "@/components/RothcoCta.vue";
    import RenameListModal from "@/components/RenameListModal.vue";
    import ListDeleteModal from "@/components/ListDeleteModal.vue";
    import ListsMassDeleteModal from "@/components/ListsMassDeleteModal.vue";
    import ListsMergeModal from "@/components/ListsMergeModal.vue";
    
    import { computed, toRefs, ref, watch, onMounted, inject } from "vue";
    import { Link, router } from "@inertiajs/vue3";
    import { useLists } from "@/helpers/composables/useList";
    import { useDateFormat, watchDeep } from "@vueuse/core";
    import { useRouter } from "@/helpers/composables/useRouter";
    import { useDialog } from "primevue/usedialog";
    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";

    import DeleteBlackIcon from "@icons/delete-black.svg";
    import PlusIcon from "@icons/plus.svg";
    interface List {
        list_id: number;
        name: string;
        total: number;
        amount: number;
        last_updated: string;
    }

    interface ListResource {
        data: List[];
        meta: {
            current_page: number;
            last_page: number;
            per_page: number;
            total: number;
        };
    }
    let props = defineProps({
        lists: {
            type: Object as PropType<ListResource>,
            default: () => ({
                data: [],
                meta: {
                    current_page: 1,
                    last_page: 1,
                    per_page: 1,
                    total: 1
                }
            })
        },
        company_users: {
            type: Array,
            default(){
                return [];
            }
        },
        current_list: {
            type: Object as PropType<List>,
            default(){
                return {};
            }
        },
    });

    let route = inject("route");

    let store = useSettingsStore();
    let { user_id:my_user_id, is_dealer, accountLevel, canSeePricing } = storeToRefs(store);

    let { lists } = toRefs(props);
    let hasListsChecked = computed(()=>checked.value.length > 0);
    let hasMultipleListsChecked = computed(()=>checked.value.length > 1);
    const tableContent = computed(()=>{
        return lists.value.data.map(list=>{
            return {
                data: {
                    ...list,
                    list_name: ["",null,undefined].includes(list.name) ? "Un-named List" : list.name,
                    last_updated: list.last_updated ? useDateFormat(list.last_updated, "MMMM D, YYYY [at] h:mm a" ).value?.replaceAll(/\"/g, "") : null,
                    is_current: list.list_id == props.current_list.list_id
                }
            }
        });
    });

    let userFilterOptions = [
        {
            value: 'All Users',
            key: 'all',
            id: 'all'
        },
        ...props.company_users.map(user => ({
            value: my_user_id.value === user.user_id ? 'My Lists' : user.username,
            key: user.user_id,
            id: user.user_id
        }))
    ];

    let listsFilterOptions = [
        {
            value: 'All Lists',
            key: "all",
            id: 'all'
        },
        {
            value: 'Open Lists',
            key: 'open',
            id: 'open'
        },
        {
            value: "Placed As Orders",
            key: 'placed',
            id: 'placed'
        }
    ];

    let visibleFields = computed(()=>{
        return [
            {
                label: "List Name",
                field: "list_name",
                sortable: true,
                is_custom: true,
                pt: {
                    headerCell: { class: 'title-xs text-left' },
                    bodyCell: { class: 'mob-w-75 text-left', 'data-label': "List Name" },
                }
            },
            {
                label: "Last Modified",
                field: "last_updated",
                sortable: true,
                pt: {
                    headerCell: { class: 'title-xs text-left' },
                    bodyCell: { class: 'mob-w-25 text-left', 'data-label': "Last Modified" },
                }
            },
            {
                label: "Is Current",
                field: "is_current",
                sortable: false,
                is_custom: true,
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                    bodyCell: { class: 'mob-w-25 text-center', 'data-label': "Is Current" },
                }
            },
            {
                label: "Rename",
                field: "rename",
                sortable: false,
                is_custom: true,
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                    bodyCell: { class: 'mob-w-25 text-center', 'data-label': "Rename" },
                }
            },
            {
                label: "Delete",
                field: "delete",
                sortable: false,
                is_custom: true,
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                    bodyCell: { class: 'mob-w-25 text-center', 'data-label': "Delete" },
                }
            },
        ]
    })

    const backendPagination = computed(()=>{
        return {
            ...props.lists.links,
            ...props.lists.meta,
        }
    })

    let checked = ref([]);
    let listsFilter = ref(listsFilterOptions[0]);
    let searchListName = ref("");
    let userSearch = ref(userFilterOptions.find((filter)=>filter.key == my_user_id.value));

    onMounted(() => {
        let params = new URLSearchParams(window.location.search);
        params.forEach((value, key) => {
            if (key == "filter[list_status]") {
                listsFilter.value = listsFilterOptions.find((filter)=>filter.key == value);
            }
            if (key == "filter[list_name]") {
                searchListName.value = value;
            }
            if (key == "filter[user]") {
                userSearch.value = value == my_user_id.value ? null : userFilterOptions.find((filter)=>filter.key == value);
            }
        });
    });

    const focused = ref(false);

    let { to } = useRouter({
        router,
        route
    });



    let dialog = useDialog();

    let openRenameList = function (list) {
        dialog.open(RenameListModal, {
            data: {
                list_id: list.list_id,
                list_name: list.list_name == 'Un-named List' ? '' : list.list_name,
                beforeClose: reloadLists
            }
        })
    };

    let openDeleteList = (list) => {
        dialog.open(ListDeleteModal, {
            data: {
                type:"list",
                list_id: list.list_id,
                beforeCloseForList: reloadLists
            }
        });
    };

    let openMassDeleteLists = () => {
        dialog.open(ListsMassDeleteModal, {
            data: {
                lists_to_delete: checked.value.map(list=>list.data),
                beforeClose: reloadLists
            }
        });
    };

    let openMergeListsModal = () => {
        dialog.open(ListsMergeModal, {
            dismissableMask: true,
            modal: true,
            showHeader: false,
            closable: false,
            data: {
                lists_to_merge: checked.value.map(list=>list.data),
                beforeClose: reloadLists
            }
        });
    }

    let reloadLists = () => {
        checked.value = [];
        to({ newParams: {}, options: { only: ["lists"], preserveState: true, preserveScroll: true } });
    };

    let { makeCurrentList, filterLists } = useLists({
        router,
        route
    });

    const clearCheckedRows = ()=>{
        checked.value = [];
    }

    function setListsFilter() {
        let filters = {
            'list_status': listsFilter.value?.key ? listsFilter.value.key : undefined,
            'list_name': searchListName.value ? searchListName.value : undefined,
            'user': userSearch.value.key === my_user_id.value ? undefined : userSearch.value.key
        };

        filterLists({
            filter: {
                ...filters
            },
            page: 1
        });
    }

    const setListNameFilter = ()=>{
        let val = searchListName.value?.trim() != "" ? searchListName.value.trim() : undefined;
        setListsFilter();
    }

    watch(listsFilter, (newVal, oldVal) => {
        let params = new URLSearchParams(window.location.search);

        if ((params.has("filter[list_status]") && params.get("filter[list_status]") != newVal.key) || !params.has("filter[list_status]")) {
            clearCheckedRows();
            setListsFilter();
        }
    });

    watch(userSearch, (newVal, oldVal) => {
        let params = new URLSearchParams(window.location.search);
        if ((params.has("filter[user]") && params.get("filter[user]") != newVal.key) || !params.has("filter[user]")) {
            clearCheckedRows();
            setListsFilter();
        }
    });

</script>
